import { FC, useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { validatePasswordAndUpdateStates } from 'src/utils/passwordValidation';

interface Props {
  password: string;
}

const PasswordValidation: FC<Props> = ({ password }) => {
  const [hasSixLetters, setHasSixLetters] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const theme = useTheme();

  //useEffect with password as dependency, checking and setting proper states by comparing the regex pattern with the password
  useEffect(() => {
    validatePasswordAndUpdateStates(
      password,
      setHasSixLetters,
      setHasUppercase,
      setHasNumber,
      setHasSpecialChar,
    );
  }, [password]);

  const initialStateStyles = {
    color: theme.palette.info.dark,
  };
  const correctStateStyles = {
    color: theme.palette.success.main,
  };
  const errorStateStyles = {
    color: theme.palette.error.main,
  };

  const successIconStyles = {
    color: theme.palette.success.main,
    transform: 'scale(0.5)',
    marginTop: '-0.25rem',
  };

  const errorIconStyles = {
    color: theme.palette.error.main,
    transform: 'scale(0.6)',
    marginTop: '-0.25rem',
  };

  const getCorrectStyles = (state: boolean) => {
    if (!password) {
      return initialStateStyles;
    }
    if (state) {
      return correctStateStyles;
    }
    if (!state) {
      return errorStateStyles;
    }
  };

  return (
    <Grid
      container
      sx={{
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
    >
      <Grid
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {hasSixLetters && <DoneIcon sx={successIconStyles} />}
        {!hasSixLetters && password && <CloseIcon sx={errorIconStyles} />}
        <Typography sx={getCorrectStyles(hasSixLetters)} variant="caption">
          Passwords must be at least 6 characters long
        </Typography>
      </Grid>
      <Grid
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {hasUppercase && <DoneIcon sx={successIconStyles} />}
        {!hasUppercase && password && <CloseIcon sx={errorIconStyles} />}
        <Typography sx={getCorrectStyles(hasUppercase)} variant="caption">
          Must contain at least one uppercase letter
        </Typography>
      </Grid>
      <Grid
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {hasNumber && <DoneIcon sx={successIconStyles} />}
        {!hasNumber && password && <CloseIcon sx={errorIconStyles} />}
        <Typography sx={getCorrectStyles(hasNumber)} variant="caption">
          Must contain at least one number
        </Typography>
      </Grid>
      <Grid
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-start',
        }}
      >
        {hasSpecialChar && <DoneIcon sx={successIconStyles} />}
        {!hasSpecialChar && password && <CloseIcon sx={errorIconStyles} />}
        <Typography sx={getCorrectStyles(hasSpecialChar)} variant="caption">
          Must contain at least one symbol (@, !, #...)
        </Typography>
      </Grid>
    </Grid>
  );
};

export default PasswordValidation;
